import { useMemo, useState } from 'react';
import PlatformBadge from './PlatformBadge';
import SortHeader from './SortHeader';
import {
  groupMeta,
  groupLabel,
  groupTypeLabel,
  formatCell,
  budgetInfo,
  sortRows,
  flatColumnsFor,
} from '../campaignObjectives';

function columnsFor(vertical) {
  const base = flatColumnsFor(vertical);
  return [base[0], { key: 'daily_budget', label: 'Ppto/día', format: 'budget' }, ...base.slice(1)];
}

function MetricCell({ col, row }) {
  if (col.format === 'budget') {
    const b = budgetInfo(row);
    return (
      <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">
        {b ? (
          <>
            {b.text}
            {b.hint && <span className="ml-1 text-xs text-gray-400">{b.hint}</span>}
          </>
        ) : (
          '—'
        )}
      </td>
    );
  }
  return (
    <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
      {formatCell(row[col.key], col.format)}
    </td>
  );
}

/**
 * Vista "Conjuntos": todos los conjuntos / grupos / asset groups juntos,
 * taggeados por objetivo, con su campaña padre. Nivel intermedio entre
 * Campañas y Anuncios. ~20 filas + scroll (header sticky), orden por columna.
 */
export default function CampaignAdGroupsTable({ adGroups, vertical }) {
  const columns = columnsFor(vertical);
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const rows = useMemo(() => sortRows(adGroups, sort.key, sort.dir), [adGroups, sort]);
  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="max-h-[52rem] overflow-auto">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-gray-200 bg-white text-xs uppercase tracking-wide text-gray-500">
              <SortHeader sortKey="group_name" sort={sort} onSort={onSort} align="left">
                Conjunto
              </SortHeader>
              <SortHeader sortKey="campaign_name" sort={sort} onSort={onSort} align="left">
                Campaña
              </SortHeader>
              <SortHeader sortKey="objective_group" sort={sort} onSort={onSort} align="left">
                Objetivo
              </SortHeader>
              {columns.map((c) => (
                <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort}>
                  {c.label}
                </SortHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((ag) => (
              <tr
                key={`${ag.platform}-${ag.group_id}`}
                className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
              >
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <PlatformBadge platform={ag.platform} status={ag.status} />
                    <span className="text-gray-900">{ag.group_name || ag.group_id}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs uppercase tracking-wide text-gray-400">
                      {groupTypeLabel(ag.group_type)}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-gray-500">{ag.campaign_name || '—'}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`rounded-md px-2 py-0.5 text-xs font-medium ${groupMeta(ag.objective_group).pill}`}
                  >
                    {groupLabel(ag.objective_group)}
                  </span>
                </td>
                {columns.map((c) => (
                  <MetricCell key={c.key} col={c} row={ag} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
